import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './Dashboard.css';
import { useTranslation } from 'react-i18next';
import Sidebar from "./common/Sidebar";
import Header from "./common/Header";
import api from '../utils/api';
import { useFetch } from '../hooks/useFetch';

export default function Goals() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'es';
  const locale = lang.startsWith('es') ? 'es-CL' : lang.startsWith('en') ? 'en-US' : lang;
  const CURRENCY = 'CLP';

  // ===== State =====
  const [editable, setEditable] = useState({}); // { [categoryName]: string|number }
  const [saveStatus, setSaveStatus] = useState(null);
  // Local per-field display text (thousand separators while typing, no extra libs)
  const [inputText, setInputText] = useState({}); // { [categoryName]: string }

  // Concurrency & retry controls for saving goals
  const isSavingGoalsRef = useRef(false);
  const inFlightGoalRef = useRef(new Map()); // key: normalized category name -> boolean

  const sleep = (ms) => new Promise(res => setTimeout(res, ms));

  // POST /goals with retries; treat 409/422 like success (upsert semantics)
  const postGoalWithRetry = async (payload, maxAttempts = 3) => {
    let attempt = 0;
    let lastErr = null;
    while (attempt < maxAttempts) {
      attempt++;
      try {
        const res = await api.post('/goals', payload);
        return { ok: true, res };
      } catch (e) {
        const status = e?.response?.status;
        if (status === 409 || status === 422) {
          return { ok: true, res: e?.response };
        }
        // retry only for network/5xx
        if (!status || (status >= 500 && status < 600)) {
          lastErr = e;
          await sleep(200 * attempt * attempt);
          continue;
        }
        lastErr = e;
        break;
      }
    }
    return { ok: false, error: lastErr };
  };

  // ===== Helpers =====
  const normalize = (name) => String(name || '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();

  // ===== Data via useFetch (robust to different return shapes) =====
  const catsDashHook   = useFetch('/dashboard/categories', { defaultValue: undefined, staleTime: 15000 });
  const catsAllHook    = useFetch('/categories',           { defaultValue: undefined, staleTime: 60000 });
  const goalsCatsHook  = useFetch('/goals/categories',     { defaultValue: undefined, staleTime: 10000 });
  const expensesHook   = useFetch('/transactions', { defaultValue: undefined, staleTime: 20000 });
  
  // Helper to extract data/loading/error from either [data, meta] or {data, loading, error}
  const pickData = (res) => Array.isArray(res) ? { data: res[0], loading: res[1]?.loading, error: res[1]?.error } 
                                               : { data: res?.data ?? res, loading: res?.loading, error: res?.error };

  // Safely trigger refetch() whether useFetch returns [data, meta] or { data, refetch }
  const callRefetch = (hookReturn) => {
    try {
      if (!hookReturn) return;
      if (typeof hookReturn.refetch === 'function') {
        hookReturn.refetch();
        return;
      }
      if (Array.isArray(hookReturn) && hookReturn[1] && typeof hookReturn[1].refetch === 'function') {
        hookReturn[1].refetch();
        return;
      }
    } catch {
      /* no-op */
    }
  };

  // Extract per-category amounts into a plain map { name: amount }
  const extractCategoryAmounts = (src) => {
    if (!src) return null;
    let cur = src;
    // Unwrap up to two nested "data" levels commonly returned by axios/backends
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;

    // Case A: { categories: { "Hogar": 123, ... } }
    if (cur && typeof cur === 'object' && cur.categories && typeof cur.categories === 'object' && !Array.isArray(cur.categories)) {
      return cur.categories;
    }
    // Case B: direct object map { "Hogar": 123, ... }
    if (cur && typeof cur === 'object' && !Array.isArray(cur)) {
      // Heuristic: if object values are numbers, treat as map
      const values = Object.values(cur);
      if (values.length && values.every(v => typeof v === 'number' || typeof v === 'string')) {
        return cur;
      }
    }
    // Case C: array of objects with name/category and amount/value
    const arr =
      (Array.isArray(cur?.categories) && cur.categories) ||
      (Array.isArray(cur) && cur);
    if (Array.isArray(arr)) {
      const map = {};
      arr.forEach(x => {
        const name = String(x?.name ?? x?.category ?? x?.category_name ?? '').trim();
        const amt  = Number(x?.amount ?? x?.value ?? x?.spent ?? x?.total ?? 0) || 0;
        if (name) map[name] = (map[name] || 0) + amt;
      });
      return Object.keys(map).length ? map : null;
    }
    return null;
  };

  // Format an integer with thousands according to current locale (no currency)
  const formatThousands = (n) => {
    try { return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(n) || 0); } catch { return String(n ?? ''); }
  };
  // Keep only digits from a string
  const digitsOnly = (s) => (s || '').replace(/\D+/g, '');

  // Extract goals into a plain map { name: goal } from various shapes
  const extractGoalsMap = (src) => {
    if (!src) return null;
    let cur = src;
    // Unwrap up to two nested "data" levels
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;

    // Case A: array of objects
    if (Array.isArray(cur)) {
      const map = {};
      cur.forEach(item => {
        const nm = String(item?.category_name ?? item?.name ?? '').trim();
        const g  = Number(item?.goal ?? item?.limit ?? 0) || 0;
        if (nm) map[nm] = g;
      });
      return Object.keys(map).length ? map : null;
    }

    // Case B: object maps on common keys
    const candidates = [
      cur?.goals,
      cur?.categories,
      cur
    ];
    for (const obj of candidates) {
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        const values = Object.values(obj);
        if (values.length && values.every(v => typeof v === 'number' || typeof v === 'string')) {
          // Numeric/string map { "Hogar": 120000, ... }
          const map = {};
          Object.entries(obj).forEach(([k, v]) => {
            const nm = String(k).trim();
            const g  = Number(v) || 0;
            if (nm) map[nm] = g;
          });
          if (Object.keys(map).length) return map;
        }
      }
    }
    return null;
  };
  
  const { data: catsDashRaw,  loading: catsDashLoading }  = pickData(catsDashHook);
  const { data: catsAllRaw, }   = pickData(catsAllHook);
  const { data: goalsCatsRaw, } = pickData(goalsCatsHook);
  const { data: expensesRaw,  loading: expensesLoading }  = pickData(expensesHook);
  
  // If /goals/categories has nothing, try /goals as fallback
  const goalsEnabled = !(Array.isArray(goalsCatsRaw) && goalsCatsRaw.length > 0);
  const goalsHook    = useFetch('/goals', { defaultValue: undefined, enabled: goalsEnabled, staleTime: 10000 });
  const { data: goalsRaw, } = pickData(goalsHook);
  
  // Compute loading while hooks resolve — include /expenses
  const loading = !!(catsDashLoading || expensesLoading);
  // Normalize date to YYYY-MM
  const ym = (d) => {
    try {
      const dt = new Date(d);
      if (isNaN(dt)) return null;
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    } catch { return null; }
  };

  // For each category, use the spend from its own latest active month
  const categoryLatestMonthSpendFromExpenses = useCallback((src) => {
    if (!src) return null;
    let cur = src;
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;
    if (cur && typeof cur === 'object' && 'data' in cur) cur = cur.data;
    if (cur && typeof cur === 'object' && Array.isArray(cur.transactions)) cur = cur.transactions;
    if (!Array.isArray(cur)) return null;

    // Build per-category month sums
    const perCat = new Map(); // cat -> Map(month -> sum)
    for (const e of cur) {
      if (!e) continue;
      const catName = (
        e.category_name ?? e.category ?? e.categoryName ??
        e?.data?.subcategory?.category?.name ??
        e?.data?.category?.name ??
        e?.data?.subcategory?.name ??
        ''
      );
      const cat = String(catName).trim();
      if (!cat) continue;
      const month = ym(e.date ?? e.posted_at ?? e.txn_date ?? e.created_at ?? e.fecha);
      if (!month) continue;
      const amt = Number(e.amount ?? e.value ?? e.total ?? e.monto ?? 0) || 0;
      if (Number.isNaN(amt)) continue;
      if (!perCat.has(cat)) perCat.set(cat, new Map());
      const m = perCat.get(cat);
      m.set(month, (m.get(month) || 0) + amt);
    }

    if (perCat.size === 0) return null;

    // For each category, pick its latest month and the corresponding sum
    const out = {};
    for (const [cat, monthsMap] of perCat.entries()) {
      const months = Array.from(monthsMap.keys());
      if (!months.length) continue;
      months.sort((a,b) => String(b).localeCompare(String(a))); // latest first
      const latest = months[0];
      out[cat] = monthsMap.get(latest) || 0;
    }
    return Object.keys(out).length ? out : null;
  }, []);

  // Index: category name (normalized) -> id (from /categories array)
  const categoriesIndex = useMemo(() => {
    const arr =
      (Array.isArray(catsAllRaw?.categories) && catsAllRaw.categories) ||
      (Array.isArray(catsAllRaw?.data?.categories) && catsAllRaw.data.categories) ||
      [];
    const map = new Map();
    arr.forEach(x => {
      const id = Number(x?.id) || 0;
      const name = String(x?.name ?? x?.category_name ?? '').trim();
      if (id && name) map.set(normalize(name), id);
    });
    return map;
  }, [catsAllRaw]);


  // Categories with spend: for each category, use its own latest active month from /expenses,
  // fallback to /dashboard/categories, then to /categories names
  const categoriesWithSpend = useMemo(() => {
    // 0) Build per-category latest-month spend from full /expenses history
    const latestPerCatMap = categoryLatestMonthSpendFromExpenses(expensesRaw);
    if (latestPerCatMap) {
      // Ensure we include all categories seen in expenses (even if some are 0 after filtering)
      const arr = Object.entries(latestPerCatMap)
        .map(([name, amount]) => ({ name: String(name).trim(), spent: Number(amount) || 0 }))
        .filter(c => c && c.name)
        .sort((a, b) => b.spent - a.spent);

      // If we also have /categories with extra categories, we could optionally merge
      // names that never had expenses with spent=0 (commented by default as the user asked "con gastos")
      return arr;
    }

    // 1) Fallback: prefer /dashboard/categories (could be last-month or totals depending on backend)
    const amounts = extractCategoryAmounts(catsDashRaw);
    if (amounts) {
      return Object.entries(amounts)
        .map(([name, amount]) => ({ name: String(name).trim(), spent: Number(amount) || 0 }))
        .filter(c => c && c.name)
        .sort((a, b) => b.spent - a.spent);
    }

    // 2) Fallback: use /categories array (names only), with spent=0
    const arr =
      (Array.isArray(catsAllRaw?.categories) && catsAllRaw.categories) ||
      (Array.isArray(catsAllRaw?.data?.categories) && catsAllRaw.data.categories) ||
      (Array.isArray(catsAllRaw) && catsAllRaw) ||
      [];
    return arr
      .map(x => ({ name: String(x?.name ?? x?.category ?? x?.category_name ?? '').trim(), spent: 0 }))
      .filter(c => c && c.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [catsAllRaw, catsDashRaw, expensesRaw, categoryLatestMonthSpendFromExpenses]);

  // Goals map from /goals/categories or /goals (supports array or object maps)
  const goals = useMemo(() => {
    const map =
      extractGoalsMap(goalsCatsRaw) ||
      extractGoalsMap(goalsRaw) ||
      {};
    return map;
  }, [goalsCatsRaw, goalsRaw]);

  // Initialize editable goals when data arrives
  useEffect(() => {
    setEditable(prev => {
      const next = { ...prev };
      categoriesWithSpend.forEach(c => {
        if (next[c.name] == null) next[c.name] = goals[c.name] ?? '';
      });
      return next;
    });
  }, [categoriesWithSpend, goals]);

  // ===== Derived =====
  const rows = useMemo(() => {
    // Only show categories we have data for
    return categoriesWithSpend.map(c => {
      const goal = Number(goals[c.name] ?? 0) || 0;
      const spent = Number(c.spent || 0);
      const pct = goal > 0 ? (spent / goal) * 100 : null;
      return { name: c.name, spent, goal, pct };
    });
  }, [categoriesWithSpend, goals]);

  // Bars color
  const barColor = (pct) => {
    if (pct == null) return 'rgba(0,0,0,0.08)';
    if (pct >= 100) return '#ef4444';
    if (pct >= 80) return '#f59e0b';
    return '#10b981';
  };

  // Detect only categories with actual changes (avoid sending unchanged)
  const dirtyEntries = useMemo(() => {
    const visible = new Set(categoriesWithSpend.map(c => c.name));
    return Object.entries(editable).filter(([cat, val]) => {
      if (!visible.has(cat)) return false;
      const newVal = val === '' ? 0 : (Number(val) || 0);
      const oldVal = Number(goals[cat] ?? 0) || 0;
      return newVal !== oldVal;
    });
  }, [editable, categoriesWithSpend, goals]);

  // Bulk save with concurrency/retry and deduplication
  const onSave = async () => {
    if (isSavingGoalsRef.current) return; // avoid parallel saves
    try {
      isSavingGoalsRef.current = true;
      setSaveStatus('saving');

      // Snapshot of entries at the moment of clicking save
      const entries = dirtyEntries;
      if (entries.length === 0) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 1000);
        return;
      }

      // Build payloads with category_id when available
      const payloads = entries.map(([cat, val]) => {
        const goal = Number(val) || 0;
        const catName = String(cat).trim();
        const id = categoriesIndex.get(normalize(catName));
        return id ? { category_id: id, category: catName, goal } : { category: catName, goal };
      });

      let anyFailed = false;

      // Sequentially save to avoid backend constraint races
      for (const p of payloads) {
        const key = normalize(p.category);
        if (inFlightGoalRef.current.get(key)) continue; // skip if in-flight for same category
        inFlightGoalRef.current.set(key, true);
        try {
          const { ok, error } = await postGoalWithRetry(p, 3);
          if (!ok) {
            anyFailed = true;
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn('[Goals] save failed for', p.category, error);
            }
          }
        } finally {
          inFlightGoalRef.current.set(key, false);
        }
      }

      // Refetch server truth so progress bars update
      callRefetch(goalsCatsHook);
      callRefetch(goalsHook);

      setSaveStatus(anyFailed ? 'error' : 'saved');
      setTimeout(() => setSaveStatus(null), anyFailed ? 2500 : 1600);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Save error', e);
      }
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2500);
    } finally {
      isSavingGoalsRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container overview" style={{ maxWidth: 'none' }}>
        <Header title={t('goals.title', 'Metas')} />
        <div className="dashboard-four-columns">
          <Sidebar />
          <div className="dashboard-main" style={{ width: '100%', maxWidth: 'none' }}>
            <div className="dashboard-section"><div>{t('common.loading', 'Cargando…')}</div></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container overview" style={{ maxWidth: 'none' }}>
      <Header title={t('goals.title', 'Metas')} />
      <div className="dashboard-four-columns">
        <Sidebar />
        <div className="dashboard-main" style={{ width: '100%', maxWidth: 'none' }}>

          <div
            className="goals-two-col"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: '1rem',
              rowGap: '1rem',
              alignItems: 'stretch',
              width: '100%',
              gridColumn: '1 / -1'
            }}
          >
          {/* Editor de metas */}
          <div className="dashboard-section" style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
            <h2 className="dashboard-section-title" style={{ fontSize: '1.2rem', margin: 0 }}>
              {t('goals.title', 'Metas')}
            </h2>
            <p style={{ margin: '0.5rem 0 0.75rem', opacity: 0.85 }}>
              {t('budgets.subtitle', 'Define límites mensuales por categoría. Se guardan en este navegador.')}
            </p>

            {rows.length === 0 && (
              <div style={{ marginTop: '0.5rem', opacity: 0.8 }}>
                {t('budgets.noCategories', 'Aún no hay categorías para presupuestar. Sube gastos o agrega categorías para empezar.')}
              </div>
            )}

            {rows.length > 0 && (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 140px) minmax(0, 160px)',
                    gap: '0.5rem',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{t('common.category', 'Categoría')}</div>
                  <div style={{ fontSize: 12, opacity: 0.7, textAlign: 'right' }}>{t('budgets.limit', 'Presupuesto CLP')}</div>
                  <div style={{ fontSize: 12, opacity: 0.7, textAlign: 'right' }}>{t('budgets.spent', 'Gastado último mes')}</div>

                  {rows.map(r => {
                    return (
                      <React.Fragment key={r.name}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                        <div style={{ textAlign: 'right' }}>
                          {(() => {
                            const currentVal = editable[r.name] != null ? editable[r.name] : (goals[r.name] ?? '');
                            const display = inputText[r.name] ?? (currentVal === '' ? '' : formatThousands(currentVal));
                            return (
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={display}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const digits = digitsOnly(raw);
                                  const nextDisplay = digits ? formatThousands(digits) : '';
                                  setInputText(prev => ({ ...prev, [r.name]: nextDisplay }));
                                  const nextNum = digits ? Number(digits) : '';
                                  setEditable(prev => ({ ...prev, [r.name]: nextNum }));
                                }}
                                onFocus={(e) => {
                                  // Seed the display text when focusing if empty
                                  if (inputText[r.name] == null && currentVal !== '') {
                                    setInputText(prev => ({ ...prev, [r.name]: formatThousands(currentVal) }));
                                  }
                                }}
                                onBlur={() => {
                                  // Commit and let future renders derive display from numeric value
                                  setInputText(prev => { const p = { ...prev }; delete p[r.name]; return p; });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    // commit local display state and blur (no auto-save)
                                    setInputText(prev => { const p = { ...prev }; delete p[r.name]; return p; });
                                    e.currentTarget.blur();
                                  }
                                }}
                                placeholder={t('budgets.example', 'ej: 120000')}
                                aria-label={t('budgets.limitFor', 'Presupuesto para {{cat}}', { cat: r.name })}
                                style={{ width: '100%', textAlign: 'right' }}
                              />
                            );
                          })()}
                        </div>
                        <div style={{ textAlign: 'right', opacity: 0.85 }}>
                          {r.spent.toLocaleString(locale, { style: 'currency', currency: CURRENCY })}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button onClick={onSave} disabled={isSavingGoalsRef.current || saveStatus === 'saving' || dirtyEntries.length === 0}>
                {saveStatus === 'saving' ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
              </button>
              {saveStatus === 'saved' && <span style={{ color: '#059669' }}>{t('budgets.saved', 'Presupuestos guardados')}</span>}
              {saveStatus === 'error' && <span style={{ color: '#dc2626' }}>{t('budgets.saveError', 'No se pudo guardar')}</span>}
            </div>
          </div>

          {/* Progreso por categoría */}
          <div className="dashboard-section" style={{ minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
            <h2 className="dashboard-section-title" style={{ fontSize: '1.2rem', margin: 0 }}>
              {t('goals.progress', 'Progreso de Metas')}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
              {rows
                .filter(r => r.goal > 0)
                .map(r => {
                  const pct = Math.round((r.spent / r.goal) * 100);
                  const color = barColor(pct);
                  const label = `${pct}% — ${r.spent.toLocaleString(locale, { style: 'currency', currency: CURRENCY })} / ${Number(r.goal||0).toLocaleString(locale, { style: 'currency', currency: CURRENCY })}`;
                  return (
                    <div key={r.name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', minWidth: 0 }}>
                        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{r.name}</div>
                        <div style={{ fontSize: 12, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{label}</div>
                      </div>
                      <div style={{ position: 'relative', height: 12, borderRadius: 8, background: 'rgba(0,0,0,0.08)', overflow: 'hidden' }} aria-label={`${r.name} ${label}`}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: (pct != null ? Math.max(0, Math.min(100, pct)) : 0) + '%', background: color, borderRadius: 8 }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          </div>
        </div>
      </div>
    </div>
  );
}